import { Accordion } from "@base-ui/react/accordion";
import { stickerUrl } from "@listou/shared/stickers";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { PhoneMockup } from "../components/landing/mockups";
import { TogetherTeaser } from "../components/landing/together-teaser";
import { Sticker } from "../components/list-page/parts";
import { SiteFooter, SiteHeader } from "../components/site-header";
import { OCCASION_DEMOS } from "../lib/demo";
import { seo } from "../lib/head";
import { OCCASION_PAGES, type LandingOccasion } from "../lib/occasion-pages";
import { peekSession } from "../lib/session";
import { themeStyle } from "../lib/theme";

export const Route = createFileRoute("/")({
  staticData: { ssr: true },
  // Signed-in visitors came for their lists, not the pitch.
  beforeLoad: async ({ context }) => {
    if (await peekSession(context.queryClient)) throw redirect({ to: "/mes-listes" });
  },
  // The home is the birth-list page: its copy, card and FAQ are the birth occasion's.
  head: () =>
    seo({
      title: BIRTH.seoTitle,
      description: BIRTH.seoDescription,
      image: BIRTH.ogImage,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Listou",
        url: "https://listou.fr/",
      },
    }),
  component: HomePage,
});

const s = stickerUrl;

/** A sticker "stuck on" the page: a slight tilt and a soft drop shadow, never in a grid. */
function Stuck({ src, className }: { src: string; className: string }) {
  return (
    <Sticker
      src={src}
      className={`absolute drop-shadow-[0_6px_10px_rgba(31,37,68,0.18)] ${className}`}
    />
  );
}

/** Straight into a birth list, on its second step; the address is asked at the end. */
const CreateCta = ({ className = "" }: { className?: string }) => (
  <Link
    to="/nouvelle-liste"
    search={{ occasion: "birth" }}
    className={`btn min-h-14 px-8 text-lg ${className}`}
  >
    Créer ma liste de naissance
  </Link>
);

const BIRTH = OCCASION_PAGES.birth;

/**
 * The home page is the birth-list page. Births are where a list is most useful (and most
 * searched for), so the hero stays still and says one thing; the other occasions get a
 * quiet section further down and their own landings.
 */
function HomePage() {
  const birth = OCCASION_DEMOS.birth;
  const palette = themeStyle(birth.list.theme, null);

  return (
    <div className="min-h-dvh overflow-x-clip bg-page">
      {/* In the hero's palette: a second button colour would compete with its call to action. */}
      <div style={palette} className="bg-page text-ink">
        <SiteHeader signedIn={false} mobileCta={<CreateCta className="w-full px-4" />} />
      </div>

      <main>
        {/* Hero -------------------------------------------------------------- */}
        <section style={palette} className="bg-page text-ink">
          <div className="mx-auto grid max-w-6xl gap-14 px-5 pt-8 pb-20 sm:px-8 lg:grid-cols-[1.1fr_1fr] lg:items-center lg:pt-14 lg:pb-24">
            <div className="grid min-w-0 justify-items-start gap-6">
              <p className="font-hand text-2xl text-accent-text sm:text-[1.7rem]">
                Tout est prêt avant l'arrivée de bébé.
              </p>
              <h1 className="font-soft max-w-[18ch] text-[2.4rem] leading-[1.06] font-medium sm:text-[4.2rem] lg:text-[3.8rem]">
                {BIRTH.h1}
              </h1>
              <p className="max-w-[46ch] text-lg leading-relaxed text-ink/80 sm:text-xl">
                {BIRTH.subtitle}
              </p>
              <div className="grid w-full gap-3 sm:flex sm:w-auto sm:flex-wrap">
                <div data-hero-cta className="grid">
                  <CreateCta />
                </div>
                <Link
                  to="/exemple"
                  search={{ occasion: "birth" }}
                  className="btn-secondary min-h-14 px-8 text-lg"
                >
                  Voir un exemple
                </Link>
              </div>
              <p className="text-muted">
                Gratuit. Vos proches n'ont besoin que du lien, pas de compte.
              </p>
            </div>

            <div className="relative mx-auto grid w-fit justify-items-center lg:mr-2 lg:ml-auto">
              {/* With the phone, as one picture: sitting on it when stacked, beside it on desktop. */}
              <img
                src="/landing/parents.webp"
                alt="Deux parents vus de dos, leur bébé endormi sur l'épaule"
                width={800}
                height={737}
                className="z-20 -mb-6 w-52 lg:absolute lg:-bottom-4 lg:-left-40 lg:mb-0 lg:w-56"
              />
              <div className="relative">
                {/* Framed on the first wish: "Déjà offert" proves the headline, the list's title doesn't. */}
                <PhoneMockup demo={birth} width={290} offset={680} className="-rotate-2" />
                <Stuck
                  src={s("bebe", "biberon")}
                  className="-top-6 -left-8 z-10 hidden w-28 -rotate-12 lg:block"
                />
                <Stuck
                  src={s("bebe", "lune")}
                  className="-top-4 -right-6 z-10 hidden w-20 rotate-12 lg:block"
                />
                <Stuck
                  src={s("bebe", "hochet")}
                  className="-right-6 -bottom-6 z-10 w-24 rotate-6 sm:w-28"
                />
              </div>
            </div>
          </div>
        </section>

        {/* How it works ------------------------------------------------------ */}
        <section className="bg-[#eef4fa]">
          <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8">
            <h2 className="font-soft text-4xl font-medium sm:text-5xl">
              Deux minutes pour vous. Un clic pour eux.
            </h2>
            <ol className="mt-12 grid gap-12 md:grid-cols-3 md:gap-8">
              <Step n={1} sticker={s("bebe", "poussette")} title="Vous notez">
                Collez le lien d'une boutique, Listou récupère photo et prix. En panne
                d'idées&nbsp;? L'assistant vous souffle l'essentiel.
              </Step>
              <Step n={2} sticker={s("bebe", "cigogne")} title="Vous partagez">
                Un lien par WhatsApp, SMS ou e-mail. Ou un QR code, directement sur le faire-part.
              </Step>
              <Step n={3} sticker={s("bebe", "nounours")} title="Ils réservent">
                En un clic, sans compte. Le cadeau est aussitôt marqué comme pris, pour tout le
                monde.
              </Step>
            </ol>
          </div>
        </section>

        {/* Surprise ---------------------------------------------------------- */}
        <section className="mx-auto grid max-w-6xl gap-12 px-5 py-24 sm:px-8 lg:grid-cols-2 lg:items-center">
          <div className="grid gap-6">
            <h2 className="font-soft text-4xl font-medium sm:text-5xl">
              Vous saurez qu'un cadeau est réservé. Pas lequel.
            </h2>
            <p className="max-w-[48ch] text-xl leading-relaxed text-ink/80">
              Listou vous prévient d'un petit mot : « un cadeau vient d'être réservé ». Ni le nom,
              ni l'objet. Le reste, vous le découvrirez en ouvrant les paquets à la maternité.
            </p>
            <aside className="relative max-w-[46ch] rounded-3xl bg-[#e6f4ee] p-6 pr-20 text-lg">
              <Stuck src={s("bebe", "tetine")} className="-top-5 -right-3 w-20 rotate-12" />
              <strong className="block">Vous préférez tout voir ?</strong>
              Coupez le mode surprise, et vous saurez ce qui est déjà pris avant d'acheter vous-même
              le transat.
            </aside>
          </div>
          <div className="grid min-w-0 grid-cols-1 items-stretch gap-6 min-[440px]:grid-cols-2">
            <SurpriseCard
              caption="Ce que voit Mamie"
              sticker={s("bebe", "nounours")}
              tilt="-rotate-2"
            >
              <p className="rounded-xl bg-[#1f2544] px-3 py-2.5 font-bold text-white">
                Déjà offert
              </p>
            </SurpriseCard>
            <SurpriseCard
              caption="Ce que vous voyez"
              sticker={s("bebe", "nounours")}
              tilt="rotate-2"
            >
              <p className="rounded-xl bg-[#1f2544]/[0.06] px-3 py-2.5 font-bold text-muted">
                Surprise gardée jusqu'à la naissance
              </p>
            </SurpriseCard>
          </div>
        </section>

        {/* Every shop --------------------------------------------------------- */}
        <section className="bg-[#e6f4ee]">
          <div className="mx-auto grid max-w-6xl gap-10 px-5 py-20 sm:px-8 lg:grid-cols-[1fr_1.1fr] lg:items-center">
            <div className="grid gap-5">
              <h2 className="font-soft text-4xl font-medium sm:text-5xl">
                Une liste de naissance multi-boutiques
              </h2>
              <p className="max-w-[46ch] text-xl leading-relaxed text-ink/80">
                La poussette d'un grand magasin, le mobile d'une créatrice, les bodies en lot : vous
                n'êtes enfermés dans aucune enseigne. Collez le lien d'un article, Listou récupère
                la photo et le prix. Un cadeau repéré en boutique ou fait main s'ajoute à la main.
              </p>
              <p className="max-w-[46ch] text-lg leading-relaxed text-ink/80">
                Vos proches achètent où ils veulent, au prix de la boutique, et réservent sur la
                liste pour que personne ne l'offre en double.{" "}
                <Link
                  to="/blog/$slug"
                  params={{ slug: "liste-de-naissance-gratuite-multi-enseignes" }}
                  className="font-bold underline"
                >
                  Pourquoi une liste multi-enseignes
                </Link>
              </p>
            </div>
            <ul className="flex flex-wrap gap-2.5" aria-label="Exemples de boutiques">
              {SHOPS.map((shop) => (
                <li
                  key={shop}
                  className="rounded-full bg-surface px-4 py-2 text-lg font-bold shadow-[0_1px_0_rgba(31,37,68,0.08)]"
                >
                  {shop}
                </li>
              ))}
              <li className="rounded-full px-4 py-2 text-lg text-muted">et toutes les autres</li>
            </ul>
          </div>
        </section>

        {/* For the grandparents --------------------------------------------- */}
        <section className="bg-[#fff6d9]">
          <div className="mx-auto grid max-w-6xl gap-12 px-5 py-20 sm:px-8 md:grid-cols-[auto_1fr] md:items-center md:gap-20">
            <div className="relative mx-auto">
              <PhoneMockup demo={birth} width={260} offset={705} className="rotate-2" />
              <Stuck src={s("bebe", "body")} className="-bottom-6 -left-10 w-24 -rotate-6" />
            </div>
            <div className="grid gap-5">
              <h2 className="font-soft text-4xl font-medium sm:text-5xl">
                Si Mamie sait ouvrir un SMS, elle sait réserver un cadeau.
              </h2>
              <p className="max-w-[40ch] text-2xl leading-relaxed">
                Gros texte, gros boutons, ni compte ni mot de passe. Un clic sur le lien, un clic
                sur le cadeau. Pas de tutoriel à faire au téléphone.
              </p>
            </div>
          </div>
        </section>

        {/* Together ---------------------------------------------------------- */}
        <section className="mx-auto max-w-6xl px-5 py-24 sm:px-8">
          <div className="relative mx-auto grid max-w-3xl justify-items-center gap-5 text-center">
            <Stuck
              src={s("bebe", "chaussons")}
              className="-top-10 left-0 w-24 -rotate-12 sm:-left-10"
            />
            <Stuck
              src={s("bebe", "hochet")}
              className="-top-12 right-0 w-24 rotate-6 sm:-right-8"
            />
            <h2 className="font-soft text-4xl font-medium sm:text-5xl">
              Une liste, deux parents, plus de « tu l'as déjà mis ? »
            </h2>
            <p className="max-w-[46ch] text-xl leading-relaxed text-ink/80">
              Invitez le co-parent : chacun ajoute ses idées depuis son téléphone, et la surprise
              reste gardée pour vous deux. La marraine peut aussi mettre la main à la pâte.
            </p>
          </div>
        </section>

        {/* Strengths ---------------------------------------------------------- */}
        <section className="bg-[#eef4fa]">
          <ul className="mx-auto grid max-w-6xl gap-10 px-5 py-20 sm:grid-cols-2 sm:px-8 lg:gap-12">
            {BIRTH.features.map((item) => (
              <li key={item.title} className="flex gap-5">
                <Sticker
                  src={item.sticker}
                  className="size-20 shrink-0 object-contain drop-shadow-[0_6px_10px_rgba(31,37,68,0.16)]"
                />
                <div className="grid content-start gap-2">
                  <h3 className="font-soft text-2xl font-semibold">{item.title}</h3>
                  <p className="max-w-[40ch] text-lg leading-relaxed text-ink/80">{item.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Together: the group pages, for the families that come for one list. */}
        <TogetherTeaser title="Et pour les fêtes à plusieurs" />

        {/* Money -------------------------------------------------------------- */}
        <section className="mx-auto max-w-6xl px-5 py-24 sm:px-8">
          <div className="relative mx-auto max-w-3xl rounded-[2rem] border-[1.5px] border-[#1f2544]/10 bg-surface p-8 sm:p-12">
            <Stuck src={s("bebe", "biberon")} className="-top-8 -left-6 w-20 -rotate-12" />
            <h2 className="font-soft text-4xl font-medium">
              Une liste de naissance gratuite. Voici comment on gagne notre vie.
            </h2>
            <p className="mt-5 text-xl leading-relaxed text-ink/80">
              Pas d'abonnement, pas de frais, pas de limite d'articles ni de réservations, pour vous
              comme pour vos proches. Quand un proche achète via un lien vers une boutique, celle-ci
              nous reverse parfois une petite commission. Le prix ne change pas d'un centime. Pas de
              publicité, et vos données ne sont pas à vendre.
            </p>
          </div>
        </section>

        {/* Guides and comparisons -------------------------------------------- */}
        <section className="mx-auto grid max-w-6xl gap-12 px-5 pb-24 sm:px-8 md:grid-cols-2">
          <div>
            <h2 className="font-soft text-3xl font-medium sm:text-4xl">
              Bien préparer sa liste de naissance
            </h2>
            <ul className="mt-6 grid gap-3 text-lg">
              {GUIDES.map((guide) => (
                <li key={guide.label}>
                  <Link {...guide.link} className="font-bold underline hover:text-accent-text">
                    {guide.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-soft text-3xl font-medium sm:text-4xl">
              Vous hésitez avec une autre liste ?
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-ink/80">
              Mes Envies, Milirose, Kadolog, Amazon : nos comparatifs point par point, sources à
              l'appui, y compris quand le concurrent fait mieux que nous.
            </p>
            <ul className="mt-5 grid gap-3 text-lg">
              {COMPARISONS.map(([slug, label]) => (
                <li key={slug}>
                  <Link
                    to="/comparatif/$slug"
                    params={{ slug }}
                    className="font-bold underline hover:text-accent-text"
                  >
                    {label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/blog/$slug"
                  params={{ slug: "meilleure-liste-de-naissance" }}
                  className="font-bold underline hover:text-accent-text"
                >
                  Le comparatif de toutes les listes de naissance
                </Link>
              </li>
            </ul>
          </div>
        </section>

        {/* FAQ ----------------------------------------------------------------- */}
        <section className="mx-auto max-w-3xl px-5 pb-24 sm:px-8">
          <h2 className="font-soft text-4xl font-medium">Questions</h2>
          {/* hiddenUntilFound: closed answers stay in the server HTML, where crawlers read them. */}
          <Accordion.Root hiddenUntilFound className="mt-8 grid gap-3">
            {BIRTH.faq.map(([question, answer]) => (
              <Accordion.Item
                key={question}
                className="rounded-2xl bg-surface shadow-[0_1px_0_rgba(31,37,68,0.08)]"
              >
                <Accordion.Header>
                  <Accordion.Trigger className="group flex w-full items-center justify-between gap-4 rounded-2xl px-5 py-4 text-left text-lg font-bold">
                    {question}
                    <span className="text-2xl text-accent transition group-data-[panel-open]:rotate-45">
                      +
                    </span>
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Panel className="h-[var(--accordion-panel-height)] overflow-hidden px-5 text-lg text-ink/80 transition-[height] data-[ending-style]:h-0 data-[starting-style]:h-0">
                  <p className="pb-5">{answer}</p>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion.Root>
        </section>

        {/* Final CTA ----------------------------------------------------------- */}
        <section className="px-5 pb-20 sm:px-8">
          <div
            style={palette}
            className="relative mx-auto grid max-w-5xl justify-items-center gap-6 rounded-[2.5rem] bg-action px-6 py-16 text-center text-action-ink"
          >
            <Stuck src={s("bebe", "cigogne")} className="-top-10 left-6 w-24 -rotate-12" />
            <Stuck src={s("bebe", "nounours")} className="-right-4 -bottom-8 w-28 rotate-12" />
            <h2 className="font-soft max-w-[18ch] text-4xl font-medium sm:text-5xl">
              Vous faites la liste. Listou s'occupe du reste.
            </h2>
            <p className="text-lg opacity-85">Gratuit, sans pub, prête en deux minutes.</p>
            <CreateCta className="!bg-surface !text-ink" />
          </div>
        </section>

        {/* Other occasions, deliberately quiet ------------------------------- */}
        <section className="mx-auto max-w-5xl px-5 pb-24 sm:px-8">
          <h2 className="font-soft text-2xl font-medium text-ink/80 sm:text-3xl">
            Listou marche aussi pour Noël, les anniversaires et les mariages
          </h2>
          <ul className="mt-6 grid gap-4 sm:grid-cols-3">
            {(["christmas", "birthday", "wedding"] as const).map((occasion) => (
              <li key={occasion}>
                <OtherOccasion occasion={occasion} />
              </li>
            ))}
          </ul>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

/** Named so a reader searching for their shop finds it; any shop's link works. */
const SHOPS = [
  "Vertbaudet",
  "Aubert",
  "Orchestra",
  "Amazon",
  "Natalys",
  "Cyrillus",
  "Petit Bateau",
  "Jacadi",
  "Decathlon",
  "Ikea",
  "Maisons du Monde",
  "Etsy",
];

const GUIDES = [
  {
    label: "Que mettre sur sa liste de naissance",
    link: { to: "/blog/$slug", params: { slug: "que-mettre-liste-de-naissance" } },
  },
  {
    label: "Idées cadeaux de naissance, rayon par rayon",
    link: { to: "/idees-cadeaux-naissance" },
  },
  {
    label: "Quand créer et partager sa liste",
    link: { to: "/blog/$slug", params: { slug: "liste-de-naissance-quand-partager" } },
  },
  {
    label: "Voir un exemple de liste de naissance",
    link: { to: "/exemple", search: { occasion: "birth" } },
  },
  { label: "Calculer sa date d'accouchement", link: { to: "/calcul-date-accouchement" } },
] as const;

const COMPARISONS = [
  ["mesenvies", "Listou ou Mes Envies"],
  ["milirose", "Listou ou Milirose"],
  ["kadolog", "Listou ou Kadolog"],
  ["amazon", "Listou ou la liste de naissance Amazon"],
] as const;

/** A compact door to another occasion's landing. */
function OtherOccasion({ occasion }: { occasion: LandingOccasion }) {
  const page = OCCASION_PAGES[occasion];
  return (
    <Link
      to={page.path}
      className="group flex h-full items-center gap-4 rounded-2xl p-4 transition hover:-translate-y-0.5"
      style={{ background: page.band }}
    >
      <Sticker src={page.heroStickers[0]} className="size-14 shrink-0 object-contain" />
      <span className="grid">
        <span className="font-soft text-lg font-semibold group-hover:underline">
          {page.linkLabel}
        </span>
        <span className="text-sm text-ink/70">{page.h1}</span>
      </span>
    </Link>
  );
}

function Step({
  n,
  sticker,
  title,
  children,
}: {
  n: number;
  sticker: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <li className="grid content-start justify-items-start gap-4">
      <div className="relative">
        <Sticker
          src={sticker}
          className="size-28 object-contain drop-shadow-[0_6px_10px_rgba(31,37,68,0.18)]"
        />
        <span className="absolute -right-2 -bottom-1 grid size-10 place-items-center rounded-full bg-[#1f2544] font-soft text-xl font-semibold text-white">
          {n}
        </span>
      </div>
      <h3 className="font-soft text-2xl font-semibold">{title}</h3>
      <p className="max-w-[36ch] text-lg leading-relaxed text-ink/80">{children}</p>
    </li>
  );
}

/**
 * The same gift as two people see it. Both cards share one structure and stretch to the
 * same height, so the only difference the eye finds is the status line.
 */
function SurpriseCard({
  caption,
  sticker,
  tilt,
  children,
}: {
  caption: string;
  sticker: string;
  tilt: string;
  children: ReactNode;
}) {
  return (
    <figure className="flex min-w-0 flex-col gap-3">
      <figcaption className="font-hand text-[1.7rem] leading-none text-muted">{caption}</figcaption>
      <div
        className={`flex min-w-0 flex-1 flex-col gap-3 rounded-2xl bg-white p-3 pb-4 shadow-[0_18px_36px_-22px_rgba(31,37,68,0.45)] ${tilt}`}
      >
        <div className="relative aspect-[4/3] min-w-0 overflow-hidden rounded-xl bg-[#fff1f4]">
          <Sticker src={sticker} className="absolute inset-0 size-full object-contain p-4" />
        </div>
        <div className="grid gap-0.5 px-1">
          <p className="font-display text-xl leading-snug font-bold">Ours en peluche Léon</p>
          <p className="text-muted">Environ 34 €</p>
        </div>
        <div className="mt-auto">{children}</div>
      </div>
    </figure>
  );
}
